// Client-side image compression to keep uploads well under proxy body-size limits.
// Resizes to a max dimension and re-encodes as JPEG. Falls back to the original
// file if anything goes wrong or the file is not a raster image we can process.

export function compressImage(file, { maxDim = 1600, quality = 0.82 } = {}) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith("image/")) return resolve(file);
    // SVG/GIF: don't rasterize (would break animation/vectors) — pass through
    if (file.type === "image/gif" || file.type === "image/svg+xml") return resolve(file);

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            // If compression didn't help (rare), keep whichever is smaller
            const finalBlob = blob.size < file.size ? blob : file;
            const name = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
            resolve(new File([finalBlob], name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      } catch {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}
